import * as RadixPopover from '@radix-ui/react-popover'
import { useRef, type ReactNode } from 'react'

import { cn } from './cn'
import { usePortalContainer } from './portal-container'
import { FLOATING_SURFACE_CLASS } from './recipes'
import { usePortalThemeClass } from './theme-scope'

/**
 * A panel anchored to what opened it.
 *
 * Distinct from `Modal`: a popover leaves the page usable behind it and closes
 * on an outside press, which is what a filter panel or a colour picker wants.
 * A dialog that must be answered before anything else is a `Modal`.
 *
 * `anchor` is for a panel that belongs to a field rather than to a button,
 * such as a calendar under a date. The panel lines up with the field, a press
 * inside the field does not close it, and on closing the focus goes back into
 * the field.
 */
export function Popover({
  trigger,
  anchor,
  children,
  align = 'start',
  side = 'bottom',
  className,
  open,
  onOpenChange,
  onOpenAutoFocus,
}: (
  | {
      /** The button that opens and closes the panel. */
      trigger: ReactNode
      anchor?: never
    }
  | {
      /** The field the panel belongs to; opening it is then the caller's. */
      anchor: ReactNode
      trigger?: never
    }
) & {
  children: ReactNode
  align?: 'start' | 'center' | 'end'
  side?: 'top' | 'right' | 'bottom' | 'left'
  className?: string
  open?: boolean
  onOpenChange?: (open: boolean) => void
  /** Runs as the panel takes the focus; `preventDefault` leaves the focus to
   *  the caller's content. */
  onOpenAutoFocus?: (event: Event) => void
}) {
  // Into the dialog where there is one: a dialog's scroll lock cancels the
  // wheel over anything portalled beside it rather than inside it, and this
  // panel is the caller's to fill — a list in one would not scroll.
  const portalContainer = usePortalContainer()
  const anchorRef = useRef<HTMLDivElement>(null)
  const pressedOutside = useRef(false)

  return (
    <RadixPopover.Root
      open={open}
      onOpenChange={next => {
        if (next) pressedOutside.current = false
        onOpenChange?.(next)
      }}
    >
      {anchor ? (
        <RadixPopover.Anchor ref={anchorRef} className="relative">
          {anchor}
        </RadixPopover.Anchor>
      ) : null}
      {trigger ? (
        <RadixPopover.Trigger asChild>{trigger}</RadixPopover.Trigger>
      ) : null}
      <RadixPopover.Portal container={portalContainer}>
        <RadixPopover.Content
          align={align}
          side={side}
          sideOffset={6}
          onOpenAutoFocus={onOpenAutoFocus}
          onInteractOutside={event => {
            if (anchorRef.current?.contains(event.target as Node)) {
              return event.preventDefault()
            }
            pressedOutside.current =
              event.detail.originalEvent.type === 'pointerdown'
          }}
          onCloseAutoFocus={event => {
            if (!anchor) return
            event.preventDefault()
            // Back into the field from the panel — a pick, Escape — but a
            // press elsewhere keeps the focus where it landed, even where
            // that is nothing.
            const outside = pressedOutside.current
            pressedOutside.current = false
            const landed = document.activeElement
            if (!outside && (!landed || landed === document.body)) {
              anchorRef.current
                ?.querySelector<HTMLElement>('input, textarea, [tabindex="0"]')
                ?.focus()
            }
          }}
          className={cn(
            usePortalThemeClass(),
            FLOATING_SURFACE_CLASS,
            'p-3',
            'outline-none',
            className,
          )}
        >
          {children}
        </RadixPopover.Content>
      </RadixPopover.Portal>
    </RadixPopover.Root>
  )
}
